import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  ArrowLeft,
  Plus,
  Trash2,
  GripVertical,
  Save,
  Eye,
  Activity,
  Loader2,
  Type,
  ListChecks,
  ToggleLeft,
  Star,
  Hash,
  AlignLeft,
  MoreVertical,
  Copy,
  ChevronUp,
  ChevronDown,
  Settings2,
  Users,
  BarChart3,
  Download,
  Circle,
  CheckSquare,
  FileDown,
  Mail,
  FileText,
  Heart,
  UserPlus,
  Upload,
  CreditCard,
  Building2,
  Search,
  FileSpreadsheet,
  Clock,
  FileUp,
  ScrollText,
  MailOpen,
  MailX
} from 'lucide-react';
import api, { authAPI, surveysAPI, filesAPI } from '../services/api';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const questionTypes = [
  { id: 'text', name: 'Short Text', icon: Type, description: 'Single line text input' },
  { id: 'textarea', name: 'Long Text', icon: AlignLeft, description: 'Multi-line text response' },
  { id: 'single_choice', name: 'Single Choice', icon: Circle, description: 'Select one option (radio)' },
  { id: 'multiple_choice', name: 'Multiple Choice', icon: CheckSquare, description: 'Select multiple options' },
  { id: 'likert', name: 'Likert Scale', icon: Star, description: 'Agreement scale (1-5)' },
  { id: 'rating', name: 'Rating', icon: Star, description: 'Star rating (1-5)' },
  { id: 'number', name: 'Number', icon: Hash, description: 'Numeric input' },
  { id: 'yes_no', name: 'Yes/No', icon: ToggleLeft, description: 'Binary choice' },
];

const SurveyBuilderPage = () => {
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const [user, setUser] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [surveyUsers, setSurveyUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [expandedUserDetails, setExpandedUserDetails] = useState({});
  const [showAssignUsers, setShowAssignUsers] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [activeTab, setActiveTab] = useState('questions');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  
  // Consent and Thank You states
  const [consentForm, setConsentForm] = useState({ 
    title: 'Consent Form', 
    content: '', 
    required: true,
    collectDocuments: false,
    documentFields: {
      panCard: true,
      cancelledCheque: true,
      accountHolderName: true,
      bankAccountNo: true,
      ifscCode: true,
      bankNameBranch: true
    }
  });
  const [thankYouMessage, setThankYouMessage] = useState({ title: 'Thank You!', content: '' });
  const [isSavingConsent, setIsSavingConsent] = useState(false);
  const [isSavingThankYou, setIsSavingThankYou] = useState(false);
  
  // Mass Email states
  const [showMassEmail, setShowMassEmail] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState({
    subject: '',
    body: ''
  });
  
  // Create User in Survey states
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: '',
    name: '',
    phone: '',
    organization: '',
    role: 'doctor',
    password: ''
  });
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  // Bulk upload states
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  // Audit trail states
  const [auditLogs, setAuditLogs] = useState([]);

  // Email tracking states
  const [emailStatus, setEmailStatus] = useState({});
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    loadData();
  }, [surveyId]);

  const loadData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/auth');
      return;
    }

    try {
      const userData = await authAPI.getCurrentUser();
      setUser(userData);
      
      const surveyData = await surveysAPI.getById(surveyId);
      setSurvey(surveyData);
      
      // Load questions
      const response = await axios.get(`${BACKEND_URL}/api/surveys/${surveyId}/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(response.data || []);
      
      // Load survey users
      const usersResponse = await surveysAPI.getUsers(surveyId);
      setSurveyUsers(usersResponse || []);
      setSelectedUserIds(usersResponse.map(u => u.id) || []);
      
      // Load analytics
      const analyticsData = await surveysAPI.getAnalytics(surveyId);
      setAnalytics(analyticsData);

      // Load analysis data for admin/agency
      if (userData.role === 'administrator' || userData.role === 'agency') {
        try {
          const analysis = await surveysAPI.getAnalysis(surveyId);
          setAnalysisData(analysis);
        } catch (e) { /* analysis not available */ }
      }

      // Load audit logs and email status for admin
      if (userData.role === 'administrator') {
        try {
          const [auditRes, emailRes] = await Promise.all([
            surveysAPI.getAuditLogs(surveyId),
            surveysAPI.getEmailStatus(surveyId),
          ]);
          setAuditLogs(auditRes.logs || []);
          setEmailStatus(emailRes || {});
        } catch (e) { /* not critical */ }
      }
      
      // Load consent form
      try {
        const consentData = await surveysAPI.getConsent(surveyId);
        if (consentData) setConsentForm(consentData);
      } catch (e) { /* No consent form yet */ }
      
      // Load thank you message
      try {
        const thankYouData = await surveysAPI.getThankYou(surveyId);
        if (thankYouData) setThankYouMessage(thankYouData);
      } catch (e) { /* No thank you message yet */ }
      
      // Load all users for assignment (admin only)
      if (userData.role === 'administrator') {
        const allUsersResponse = await axios.get(`${BACKEND_URL}/api/users/`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAllUsers(allUsersResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading survey:', error);
      if (error.response?.status === 401) navigate('/auth');
      if (error.response?.status === 404) navigate('/surveys');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddQuestion = (type) => {
    const newQuestion = {
      id: `temp_${Date.now()}`,
      type: type.id,
      text: '',
      description: '',
      required: false,
      options: type.id === 'single_choice' || type.id === 'multiple_choice' ? ['Option 1', 'Option 2'] : [],
      order: questions.length
    };
    setQuestions([...questions, newQuestion]);
    setEditingQuestion(newQuestion.id);
    setShowAddQuestion(false);
  };

  const handleUpdateQuestion = (id, updates) => {
    setQuestions(questions.map(q => q.id === id ? { ...q, ...updates } : q));
  };

  const handleDeleteQuestion = (id) => {
    setQuestions(questions.filter(q => q.id !== id));
  };

  const handleDuplicateQuestion = (question) => {
    const newQuestion = {
      ...question,
      id: `temp_${Date.now()}`,
      text: `${question.text} (Copy)`,
      order: questions.length
    };
    setQuestions([...questions, newQuestion]);
  };

  const handleMoveQuestion = (index, direction) => {
    const newQuestions = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= questions.length) return;
    [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
    setQuestions(newQuestions.map((q, i) => ({ ...q, order: i })));
  };

  const handleAddOption = (questionId) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return { ...q, options: [...q.options, `Option ${q.options.length + 1}`] };
      }
      return q;
    }));
  };

  const handleUpdateOption = (questionId, optionIndex, value) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        const newOptions = [...q.options];
        newOptions[optionIndex] = value;
        return { ...q, options: newOptions };
      }
      return q;
    }));
  };

  const handleDeleteOption = (questionId, optionIndex) => {
    setQuestions(questions.map(q => {
      if (q.id === questionId) {
        return { ...q, options: q.options.filter((_, i) => i !== optionIndex) };
      }
      return q;
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/surveys/${surveyId}/questions`,
        { questions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Questions saved successfully!');
    } catch (error) {
      console.error('Error saving questions:', error);
      alert('Failed to save questions: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleAssignUsers = async () => {
    try {
      await surveysAPI.assignUsers(surveyId, selectedUserIds);
      const usersResponse = await surveysAPI.getUsers(surveyId);
      setSurveyUsers(usersResponse || []);
      setShowAssignUsers(false);
      alert('Users assigned successfully!');
    } catch (error) {
      alert('Failed to assign users: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleExportReport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/surveys/${surveyId}/export/report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey_report_${survey.title.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to export report');
    }
  };

  const handleSaveConsent = async () => {
    setIsSavingConsent(true);
    try {
      await surveysAPI.updateConsent(surveyId, consentForm);
      alert('Consent form saved successfully!');
    } catch (error) {
      alert('Failed to save consent form: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSavingConsent(false);
    }
  };

  const handleSaveThankYou = async () => {
    setIsSavingThankYou(true);
    try {
      await surveysAPI.updateThankYou(surveyId, thankYouMessage);
      alert('Thank you message saved successfully!');
    } catch (error) {
      alert('Failed to save thank you message: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSavingThankYou(false);
    }
  };

  const handleSendMassEmail = async () => {
    setSendingEmails(true);
    try {
      const result = await surveysAPI.sendMassEmail(surveyId, emailTemplate.subject || `${survey?.title} - Survey Invitation`);
      const msg = result.email_configured
        ? `Emails sent to ${result.sent}/${result.total_doctors} doctors.${result.failed > 0 ? ` ${result.failed} failed.` : ''}`
        : `Email service not configured (RESEND_API_KEY missing). ${result.total_doctors} emails tracked but not delivered.`;
      alert(msg);
      setShowMassEmail(false);
      // Refresh email status
      const emailRes = await surveysAPI.getEmailStatus(surveyId);
      setEmailStatus(emailRes || {});
    } catch (error) {
      alert('Failed to send emails: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingEmails(false);
    }
  };

  const handleBulkUpload = async (file) => {
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const result = await surveysAPI.bulkUploadUsers(surveyId, file);
      setBulkResult(result);
      // Reload survey users
      loadData();
    } catch (error) {
      setBulkResult({ error: error.response?.data?.detail || error.message });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleCreateUserInSurvey = async () => {
    if (!newUserForm.username.trim() || !newUserForm.email.trim() || !newUserForm.password.trim()) {
      alert('Please fill in all required fields');
      return;
    }
    
    setIsCreatingUser(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${BACKEND_URL}/api/users/`, newUserForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const newUser = response.data;
      
      // Auto-assign user to this survey
      const newUserIds = [...selectedUserIds, newUser.id];
      await surveysAPI.assignUsers(surveyId, newUserIds);
      
      // Refresh users list
      const usersResponse = await surveysAPI.getUsers(surveyId);
      setSurveyUsers(usersResponse || []);
      setSelectedUserIds(newUserIds);
      
      // Reset form
      setNewUserForm({
        username: '',
        email: '',
        name: '',
        phone: '',
        organization: '',
        role: 'doctor',
        password: ''
      });
      setShowCreateUser(false);
      alert('User created and assigned to survey!');
    } catch (error) {
      alert('Failed to create user: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsCreatingUser(false);
    }
  };

  const getQuestionTypeInfo = (typeId) => {
    return questionTypes.find(t => t.id === typeId) || questionTypes[0];
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      administrator: 'bg-amber-500/10 text-amber-400',
      doctor: 'bg-blue-500/10 text-blue-400',
      agency: 'bg-purple-500/10 text-purple-400',
      sponsor: 'bg-emerald-500/10 text-emerald-400'
    };
    return colors[role] || 'bg-slate-500/10 text-slate-400';
  };

  const isAdmin = user?.role === 'administrator';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-slate-900 border-b border-slate-800">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" onClick={() => navigate('/surveys')} className="text-slate-400 hover:text-white flex-shrink-0">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="min-w-0">
                <h1 className="text-lg sm:text-xl font-bold text-white truncate">{survey?.title}</h1>
                <p className="text-sm text-slate-400">Survey Builder</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap pl-10 sm:pl-0">
              <Button variant="outline" onClick={handleExportReport} className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs sm:text-sm">
                <FileDown className="w-4 h-4 mr-1 sm:mr-2" /> Export
              </Button>
              <Button variant="outline" onClick={() => setShowPreview(true)} className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs sm:text-sm">
                <Eye className="w-4 h-4 mr-1 sm:mr-2" /> Preview
              </Button>
              <Button onClick={handleSave} disabled={isSaving} className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-xs sm:text-sm">
                {isSaving ? <Loader2 className="w-4 h-4 mr-1 sm:mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-1 sm:mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto p-3 sm:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-900 border border-slate-800 p-1 w-full overflow-x-auto flex whitespace-nowrap">
            <TabsTrigger value="questions" className="data-[state=active]:bg-slate-800">
              <ListChecks className="w-4 h-4 mr-2" /> Questions
            </TabsTrigger>
            <TabsTrigger value="consent" className="data-[state=active]:bg-slate-800">
              <FileText className="w-4 h-4 mr-2" /> Consent Form
            </TabsTrigger>
            <TabsTrigger value="thankyou" className="data-[state=active]:bg-slate-800">
              <Heart className="w-4 h-4 mr-2" /> Thank You
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-slate-800">
              <Users className="w-4 h-4 mr-2" /> Assigned Users ({surveyUsers.length})
            </TabsTrigger>
            <TabsTrigger value="analytics" className="data-[state=active]:bg-slate-800">
              <BarChart3 className="w-4 h-4 mr-2" /> Analytics
            </TabsTrigger>
            <TabsTrigger value="analysis" className="data-[state=active]:bg-slate-800" data-testid="data-analysis-tab">
              <Activity className="w-4 h-4 mr-2" /> Data Analysis
            </TabsTrigger>
            {user?.role === 'administrator' && (
              <TabsTrigger value="audit" className="data-[state=active]:bg-slate-800" data-testid="audit-trail-tab">
                <ScrollText className="w-4 h-4 mr-2" /> Audit Trail
              </TabsTrigger>
            )}
          </TabsList>

          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-4">
            {questions.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800 border-dashed">
                <CardContent className="py-12 text-center">
                  <ListChecks className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-400 mb-4">No questions yet. Add your first question to get started.</p>
                  <Button onClick={() => setShowAddQuestion(true)} className="bg-gradient-to-r from-blue-500 to-cyan-400">
                    <Plus className="w-4 h-4 mr-2" /> Add Question
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {questions.map((question, index) => {
                  const typeInfo = getQuestionTypeInfo(question.type);
                  const TypeIcon = typeInfo.icon;
                  const isEditing = editingQuestion === question.id;

                  return (
                    <Card key={question.id} className={`bg-slate-900 border-slate-800 ${isEditing ? 'ring-2 ring-cyan-500' : ''}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="flex flex-col items-center gap-1 pt-1">
                            <button onClick={() => handleMoveQuestion(index, 'up')} disabled={index === 0} className="text-slate-600 hover:text-slate-400 disabled:opacity-30">
                              <ChevronUp className="w-4 h-4" />
                            </button>
                            <GripVertical className="w-4 h-4 text-slate-600" />
                            <button onClick={() => handleMoveQuestion(index, 'down')} disabled={index === questions.length - 1} className="text-slate-600 hover:text-slate-400 disabled:opacity-30">
                              <ChevronDown className="w-4 h-4" />
                            </button>
                          </div>

                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-3">
                              <Badge className="bg-slate-800 text-slate-300">
                                <TypeIcon className="w-3 h-3 mr-1" /> {typeInfo.name}
                              </Badge>
                              <Badge variant="outline" className="text-slate-500">Q{index + 1}</Badge>
                              {question.required && <Badge className="bg-red-500/10 text-red-400">Required</Badge>}
                            </div>

                            {isEditing ? (
                              <div className="space-y-4">
                                <div>
                                  <Label className="text-slate-300 text-sm">Question Text *</Label>
                                  <Input
                                    value={question.text}
                                    onChange={(e) => handleUpdateQuestion(question.id, { text: e.target.value })}
                                    placeholder="Enter your question..."
                                    className="bg-slate-800 border-slate-700 text-white mt-1"
                                  />
                                </div>
                                <div>
                                  <Label className="text-slate-300 text-sm">Description (optional)</Label>
                                  <Input
                                    value={question.description || ''}
                                    onChange={(e) => handleUpdateQuestion(question.id, { description: e.target.value })}
                                    placeholder="Add a description or instructions..."
                                    className="bg-slate-800 border-slate-700 text-white mt-1"
                                  />
                                </div>

                                {(question.type === 'single_choice' || question.type === 'multiple_choice') && (
                                  <div>
                                    <Label className="text-slate-300 text-sm">Options</Label>
                                    <div className="space-y-2 mt-2">
                                      {question.options.map((option, optIndex) => (
                                        <div key={optIndex} className="flex items-center gap-2">
                                          <Input
                                            value={option}
                                            onChange={(e) => handleUpdateOption(question.id, optIndex, e.target.value)}
                                            className="bg-slate-800 border-slate-700 text-white"
                                          />
                                          <Button variant="ghost" size="sm" onClick={() => handleDeleteOption(question.id, optIndex)} className="text-red-400 hover:text-red-300">
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      ))}
                                      <Button variant="outline" size="sm" onClick={() => handleAddOption(question.id)} className="border-slate-700 text-slate-400">
                                        <Plus className="w-3 h-3 mr-1" /> Add Option
                                      </Button>
                                    </div>
                                  </div>
                                )}

                                <div className="flex items-center gap-2">
                                  <Switch
                                    checked={question.required}
                                    onCheckedChange={(checked) => handleUpdateQuestion(question.id, { required: checked })}
                                  />
                                  <Label className="text-slate-400 text-sm">Required question</Label>
                                </div>

                                <Button onClick={() => setEditingQuestion(null)} className="bg-slate-800 hover:bg-slate-700 text-white">
                                  Done Editing
                                </Button>
                              </div>
                            ) : (
                              <div>
                                <p className="text-white font-medium">{question.text || '(No question text)'}</p>
                                {question.description && <p className="text-slate-500 text-sm mt-1">{question.description}</p>}
                                {(question.type === 'single_choice' || question.type === 'multiple_choice') && question.options.length > 0 && (
                                  <div className="mt-2 space-y-1">
                                    {question.options.map((opt, i) => (
                                      <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                                        {question.type === 'single_choice' ? (
                                          <Circle className="w-4 h-4" />
                                        ) : (
                                          <CheckSquare className="w-4 h-4" />
                                        )}
                                        {opt}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-slate-900 border-slate-800">
                              <DropdownMenuItem onClick={() => setEditingQuestion(question.id)} className="text-slate-300">
                                <Settings2 className="w-4 h-4 mr-2" /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicateQuestion(question)} className="text-slate-300">
                                <Copy className="w-4 h-4 mr-2" /> Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDeleteQuestion(question.id)} className="text-red-400">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                <Button onClick={() => setShowAddQuestion(true)} variant="outline" className="w-full border-dashed border-slate-700 text-slate-400 hover:text-white hover:bg-slate-900 h-14">
                  <Plus className="w-5 h-5 mr-2" /> Add Question
                </Button>
              </>
            )}
          </TabsContent>

          {/* Consent Form Tab */}
          <TabsContent value="consent" className="space-y-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <FileText className="w-5 h-5 text-cyan-400" /> Consent Form Builder
                </CardTitle>
                <p className="text-sm text-slate-400">Create a consent form that will be shown to respondents before they can fill out the survey.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-slate-300">Consent Form Title</Label>
                  <Input
                    value={consentForm.title}
                    onChange={(e) => setConsentForm({ ...consentForm, title: e.target.value })}
                    placeholder="e.g., Informed Consent Form"
                    className="bg-slate-800 border-slate-700 text-white mt-2"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Consent Form Content</Label>
                  <Textarea
                    value={consentForm.content}
                    onChange={(e) => setConsentForm({ ...consentForm, content: e.target.value })}
                    placeholder="Enter the full consent form text that respondents must read and agree to before participating..."
                    className="bg-slate-800 border-slate-700 text-white mt-2 min-h-[300px]"
                  />
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={consentForm.required}
                    onCheckedChange={(checked) => setConsentForm({ ...consentForm, required: checked })}
                  />
                  <Label className="text-slate-400">Consent is required to participate</Label>
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveConsent} 
                    disabled={isSavingConsent}
                    className="bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500"
                  >
                    {isSavingConsent ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Consent Form
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Document Collection Section */}
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Upload className="w-5 h-5 text-amber-400" /> Document & Banking Details Collection
                    </CardTitle>
                    <p className="text-sm text-slate-400 mt-1">Enable collection of documents and banking details from respondents (for honorarium payments).</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-sm ${consentForm.collectDocuments ? 'text-amber-400' : 'text-slate-500'}`}>
                      {consentForm.collectDocuments ? 'Enabled' : 'Disabled'}
                    </span>
                    <Switch
                      checked={consentForm.collectDocuments || false}
                      onCheckedChange={(checked) => setConsentForm({ ...consentForm, collectDocuments: checked })}
                    />
                  </div>
                </div>
              </CardHeader>
              
              {consentForm.collectDocuments && (
                <CardContent className="space-y-6">
                  {/* Document Uploads */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <CreditCard className="w-4 h-4 text-cyan-400" /> Document Uploads
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">Pan Card</p>
                            <p className="text-xs text-slate-400">Upload PAN card image/PDF</p>
                          </div>
                        </div>
                        <Switch
                          checked={consentForm.documentFields?.panCard ?? true}
                          onCheckedChange={(checked) => setConsentForm({ 
                            ...consentForm, 
                            documentFields: { ...consentForm.documentFields, panCard: checked }
                          })}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div>
                            <p className="text-white font-medium">Cancelled Cheque</p>
                            <p className="text-xs text-slate-400">Upload cancelled cheque image/PDF</p>
                          </div>
                        </div>
                        <Switch
                          checked={consentForm.documentFields?.cancelledCheque ?? true}
                          onCheckedChange={(checked) => setConsentForm({ 
                            ...consentForm, 
                            documentFields: { ...consentForm.documentFields, cancelledCheque: checked }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Banking Details Fields */}
                  <div className="space-y-4">
                    <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-purple-400" /> Banking Details Fields
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                        <div>
                          <p className="text-white font-medium">Name of Account Holder</p>
                          <p className="text-xs text-slate-400">Full name as per bank account</p>
                        </div>
                        <Switch
                          checked={consentForm.documentFields?.accountHolderName ?? true}
                          onCheckedChange={(checked) => setConsentForm({ 
                            ...consentForm, 
                            documentFields: { ...consentForm.documentFields, accountHolderName: checked }
                          })}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                        <div>
                          <p className="text-white font-medium">Bank Account No.</p>
                          <p className="text-xs text-slate-400">Numeric bank account number</p>
                        </div>
                        <Switch
                          checked={consentForm.documentFields?.bankAccountNo ?? true}
                          onCheckedChange={(checked) => setConsentForm({ 
                            ...consentForm, 
                            documentFields: { ...consentForm.documentFields, bankAccountNo: checked }
                          })}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                        <div>
                          <p className="text-white font-medium">IFSC Code</p>
                          <p className="text-xs text-slate-400">11-character bank IFSC code</p>
                        </div>
                        <Switch
                          checked={consentForm.documentFields?.ifscCode ?? true}
                          onCheckedChange={(checked) => setConsentForm({ 
                            ...consentForm, 
                            documentFields: { ...consentForm.documentFields, ifscCode: checked }
                          })}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl border border-slate-700">
                        <div>
                          <p className="text-white font-medium">Bank Name & Branch</p>
                          <p className="text-xs text-slate-400">Bank name with branch location</p>
                        </div>
                        <Switch
                          checked={consentForm.documentFields?.bankNameBranch ?? true}
                          onCheckedChange={(checked) => setConsentForm({ 
                            ...consentForm, 
                            documentFields: { ...consentForm.documentFields, bankNameBranch: checked }
                          })}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                    <p className="text-sm text-amber-300">
                      <strong>Note:</strong> When enabled, respondents will be asked to provide these details after giving consent and before starting the survey. All documents and banking details will be stored securely.
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <Button 
                      onClick={handleSaveConsent} 
                      disabled={isSavingConsent}
                      className="bg-gradient-to-r from-amber-500 to-orange-400 hover:from-amber-600 hover:to-orange-500"
                    >
                      {isSavingConsent ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Document Settings
                    </Button>
                  </div>
                </CardContent>
              )}
            </Card>
          </TabsContent>

          {/* Thank You Message Tab */}
          <TabsContent value="thankyou" className="space-y-6">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Heart className="w-5 h-5 text-pink-400" /> Thank You Message Builder
                </CardTitle>
                <p className="text-sm text-slate-400">Create a custom thank you message that will be displayed after respondents complete the survey.</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-slate-300">Thank You Title</Label>
                  <Input
                    value={thankYouMessage.title}
                    onChange={(e) => setThankYouMessage({ ...thankYouMessage, title: e.target.value })}
                    placeholder="e.g., Thank You!"
                    className="bg-slate-800 border-slate-700 text-white mt-2"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Thank You Message</Label>
                  <Textarea
                    value={thankYouMessage.content}
                    onChange={(e) => setThankYouMessage({ ...thankYouMessage, content: e.target.value })}
                    placeholder="Enter your thank you message that will be displayed after survey completion..."
                    className="bg-slate-800 border-slate-700 text-white mt-2 min-h-[200px]"
                  />
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    onClick={handleSaveThankYou} 
                    disabled={isSavingThankYou}
                    className="bg-gradient-to-r from-pink-500 to-purple-400 hover:from-pink-600 hover:to-purple-500"
                  >
                    {isSavingThankYou ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Thank You Message
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-white">Assigned Users</h2>
              {isAdmin && (
                <div className="flex items-center gap-2 flex-wrap">
                  <Button onClick={() => setShowMassEmail(true)} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <Mail className="w-4 h-4 mr-2" /> Send Mass Email
                  </Button>
                  <Button onClick={() => setShowBulkUpload(true)} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="bulk-upload-btn">
                    <FileUp className="w-4 h-4 mr-2" /> Bulk Upload
                  </Button>
                  <Button onClick={() => setShowCreateUser(true)} variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
                    <UserPlus className="w-4 h-4 mr-2" /> Create & Add User
                  </Button>
                  <Button onClick={() => setShowAssignUsers(true)} className="bg-gradient-to-r from-blue-500 to-cyan-400">
                    <Users className="w-4 h-4 mr-2" /> Assign Existing Users
                  </Button>
                </div>
              )}
            </div>
            
            {surveyUsers.length === 0 ? (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-400">No users assigned to this survey yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-3">
                {/* Status Legend and Bulk Actions */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-400">Status:</span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      <span className="text-slate-400">Not Started</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      <span className="text-slate-400">In Progress</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      <span className="text-slate-400">Completed</span>
                    </span>
                  </div>
                  
                  {/* Bulk Export Actions */}
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await surveysAPI.exportDocumentsZip(surveyId);
                          const blob = new Blob([response.data], { type: 'application/zip' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `documents_${survey?.title?.replace(/\s+/g, '_') || 'survey'}_${new Date().toISOString().split('T')[0]}.zip`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch (error) {
                          const msg = error.response?.status === 404 ? 'No uploaded documents found for this survey' : 'Failed to export documents';
                          alert(msg);
                        }
                      }}
                      data-testid="export-documents-zip-btn"
                      className="bg-gradient-to-r from-purple-500 to-violet-400 hover:from-purple-600 hover:to-violet-500"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download All Documents (ZIP)
                    </Button>
                    <Button
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await surveysAPI.exportBankingDetails(surveyId, true);
                          const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `banking_details_${survey?.title?.replace(/\s+/g, '_') || 'survey'}_${new Date().toISOString().split('T')[0]}.xlsx`;
                          a.click();
                          URL.revokeObjectURL(url);
                        } catch (error) {
                          console.error('Export failed:', error);
                          alert('Failed to export banking details');
                        }
                      }}
                      data-testid="export-banking-details-btn"
                      className="bg-gradient-to-r from-emerald-500 to-teal-400 hover:from-emerald-600 hover:to-teal-500"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Export Banking Details (Excel)
                    </Button>
                  </div>
                </div>
                
                {surveyUsers.map((u) => (
                  <Card key={u.id} className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-semibold ${
                            u.completion_status === 'completed' 
                              ? 'bg-gradient-to-br from-emerald-500/20 to-green-500/20 text-emerald-400'
                              : u.completion_status === 'in_progress'
                                ? 'bg-gradient-to-br from-amber-500/20 to-yellow-500/20 text-amber-400'
                                : 'bg-gradient-to-br from-red-500/20 to-rose-500/20 text-red-400'
                          }`}>
                            {u.name?.charAt(0) || 'U'}
                          </div>
                          <div>
                            <p className="font-medium text-white">
                              {u.role === 'doctor' ? `Dr. ${u.name}` : u.name}
                            </p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {/* Banking Details Toggle Button */}
                          {(u.banking_details || u.documents) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setExpandedUserDetails(prev => ({
                                ...prev,
                                [u.id]: !prev[u.id]
                              }))}
                              className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                            >
                              <CreditCard className="w-4 h-4 mr-1" />
                              Details
                              {expandedUserDetails[u.id] ? (
                                <ChevronUp className="w-4 h-4 ml-1" />
                              ) : (
                                <ChevronDown className="w-4 h-4 ml-1" />
                              )}
                            </Button>
                          )}
                          {/* Completion Status Badge */}
                          <Badge className={
                            u.completion_status === 'completed'
                              ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                              : u.completion_status === 'in_progress'
                                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                          }>
                            {u.completion_status === 'completed' && '✓ Completed'}
                            {u.completion_status === 'in_progress' && `⏳ In Progress (${u.progress}%)`}
                            {u.completion_status === 'not_started' && '○ Not Started'}
                          </Badge>
                          {/* Email Open Status Badge */}
                          {emailStatus[u.username] && (
                            <Badge className={
                              emailStatus[u.username].opened
                                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                                : 'bg-red-500/20 text-red-400 border border-red-500/30'
                            } data-testid={`email-status-${u.username}`}>
                              {emailStatus[u.username].opened
                                ? <><MailOpen className="w-3 h-3 mr-1 inline" /> Opened</>
                                : <><MailX className="w-3 h-3 mr-1 inline" /> Not Opened</>
                              }
                            </Badge>
                          )}
                          {u.phone && <span className="text-sm text-slate-400 hidden md:inline">{u.phone}</span>}
                          <Badge className={getRoleBadgeColor(u.role)}>
                            {u.role?.charAt(0).toUpperCase() + u.role?.slice(1)}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* Submission Time */}
                      {u.submitted_at && u.completion_status === 'completed' && (
                        <p className="text-xs text-slate-500 mt-2 ml-13">
                          Submitted: {new Date(u.submitted_at).toLocaleDateString()} at {new Date(u.submitted_at).toLocaleTimeString()}
                        </p>
                      )}
                      
                      {/* Expanded Banking Details & Documents Section */}
                      {expandedUserDetails[u.id] && (u.banking_details || u.documents) && (
                        <div className="mt-4 pt-4 border-t border-slate-800">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Documents Section */}
                            {u.documents && (
                              <div className="bg-slate-800/50 rounded-xl p-4">
                                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                                  <Upload className="w-4 h-4 text-cyan-400" />
                                  Uploaded Documents
                                </h4>
                                <div className="space-y-2">
                                  {u.documents.pan_card && (
                                    <div className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg">
                                      <div className="flex items-center gap-2">
                                        <CreditCard className="w-4 h-4 text-amber-400" />
                                        <span className="text-slate-300 text-sm">Pan Card</span>
                                        {u.documents.pan_card_exists === false && (
                                          <Badge className="bg-red-500/20 text-red-400 text-[10px]">File Missing</Badge>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-500 truncate max-w-[100px]">{u.documents.pan_card}</span>
                                        {u.documents.pan_card_exists ? (
                                          <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="h-7 w-7 p-0 text-cyan-400 hover:text-cyan-300"
                                            onClick={async () => {
                                              const url = u.documents.pan_card_url || filesAPI.getDownloadUrl(u.username, u.documents.pan_card);
                                              try { await filesAPI.download(url); } catch (err) { alert('Download failed: ' + (err.message || 'Unknown error')); }
                                            }}
                                          >
                                            <Download className="w-4 h-4" />
                                          </Button>
                                        ) : (
                                          <label className="cursor-pointer">
                                            <input
                                              type="file"
                                              accept=".jpg,.jpeg,.png,.pdf"
                                              className="hidden"
                                              onChange={async (e) => {
                                                if (e.target.files[0]) {
                                                  try {
                                                    await filesAPI.uploadForUser('pan_card', u.username, e.target.files[0]);
                                                    alert('Pan Card uploaded successfully! Refresh to see changes.');
                                                    window.location.reload();
                                                  } catch (err) {
                                                    alert('Failed to upload: ' + (err.response?.data?.detail || err.message));
                                                  }
                                                }
                                              }}
                                            />
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-400 hover:text-amber-300" asChild>
                                              <span><Upload className="w-4 h-4 mr-1" /> Upload</span>
                                            </Button>
                                          </label>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {u.documents.cancelled_cheque && (
                                    <div className="flex items-center justify-between p-2 bg-slate-900/50 rounded-lg">
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-emerald-400" />
                                        <span className="text-slate-300 text-sm">Cancelled Cheque</span>
                                        {u.documents.cancelled_cheque_exists === false && (
                                          <Badge className="bg-red-500/20 text-red-400 text-[10px]">File Missing</Badge>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-slate-500 truncate max-w-[100px]">{u.documents.cancelled_cheque}</span>
                                        {u.documents.cancelled_cheque_exists ? (
                                          <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="h-7 w-7 p-0 text-cyan-400 hover:text-cyan-300"
                                            onClick={async () => {
                                              const url = u.documents.cancelled_cheque_url || filesAPI.getDownloadUrl(u.username, u.documents.cancelled_cheque);
                                              try { await filesAPI.download(url); } catch (err) { alert('Download failed: ' + (err.message || 'Unknown error')); }
                                            }}
                                          >
                                            <Download className="w-4 h-4" />
                                          </Button>
                                        ) : (
                                          <label className="cursor-pointer">
                                            <input
                                              type="file"
                                              accept=".jpg,.jpeg,.png,.pdf"
                                              className="hidden"
                                              onChange={async (e) => {
                                                if (e.target.files[0]) {
                                                  try {
                                                    await filesAPI.uploadForUser('cancelled_cheque', u.username, e.target.files[0]);
                                                    alert('Cancelled Cheque uploaded successfully! Refresh to see changes.');
                                                    window.location.reload();
                                                  } catch (err) {
                                                    alert('Failed to upload: ' + (err.response?.data?.detail || err.message));
                                                  }
                                                }
                                              }}
                                            />
                                            <Button size="sm" variant="ghost" className="h-7 px-2 text-amber-400 hover:text-amber-300" asChild>
                                              <span><Upload className="w-4 h-4 mr-1" /> Upload</span>
                                            </Button>
                                          </label>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  {!u.documents.pan_card && !u.documents.cancelled_cheque && (
                                    <p className="text-slate-500 text-sm">No documents uploaded</p>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Banking Details Section */}
                            {u.banking_details && (
                              <div className="bg-slate-800/50 rounded-xl p-4">
                                <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                                  <Building2 className="w-4 h-4 text-purple-400" />
                                  Banking Details
                                </h4>
                                <div className="space-y-2 text-sm">
                                  {u.banking_details.account_holder_name && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-400">Account Holder:</span>
                                      <span className="text-white font-medium">{u.banking_details.account_holder_name}</span>
                                    </div>
                                  )}
                                  {u.banking_details.bank_account_no && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-400">Account No:</span>
                                      <span className="text-white font-mono">{u.banking_details.bank_account_no}</span>
                                    </div>
                                  )}
                                  {u.banking_details.ifsc_code && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-400">IFSC Code:</span>
                                      <span className="text-white font-mono">{u.banking_details.ifsc_code}</span>
                                    </div>
                                  )}
                                  {u.banking_details.bank_name_branch && (
                                    <div className="flex justify-between">
                                      <span className="text-slate-400">Bank & Branch:</span>
                                      <span className="text-white text-right max-w-[200px]">{u.banking_details.bank_name_branch}</span>
                                    </div>
                                  )}
                                  {!u.banking_details.account_holder_name && !u.banking_details.bank_account_no && (
                                    <p className="text-slate-500">No banking details provided</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Export Button */}
                          <div className="mt-4 flex justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const data = {
                                  doctor_name: u.role === 'doctor' ? `Dr. ${u.name}` : u.name,
                                  email: u.email,
                                  phone: u.phone,
                                  ...u.banking_details,
                                  documents: u.documents
                                };
                                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                                const url = URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${u.name.replace(/\s+/g, '_')}_banking_details.json`;
                                a.click();
                                URL.revokeObjectURL(url);
                              }}
                              className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Export Details
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">
            {analytics && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400">Total Responses</p>
                      <p className="text-2xl font-bold text-white">{analytics.total_responses}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400">Target Responses</p>
                      <p className="text-2xl font-bold text-white">{analytics.target_responses}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400">Completion Rate</p>
                      <p className="text-2xl font-bold text-cyan-400">{analytics.completion_rate}%</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400">Assigned Users</p>
                      <p className="text-2xl font-bold text-white">{analytics.assigned_users_count}</p>
                    </CardContent>
                  </Card>
                </div>

                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white">Users by Role</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {Object.keys(analytics.users_by_role).length === 0 ? (
                      <p className="text-slate-500">No users assigned</p>
                    ) : (
                      <div className="space-y-3">
                        {Object.entries(analytics.users_by_role).map(([role, count]) => (
                          <div key={role} className="flex items-center justify-between">
                            <span className="text-slate-300 capitalize">{role}</span>
                            <Badge className={getRoleBadgeColor(role)}>{count}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-slate-900 border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-white">Questions Summary</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-400 mb-4">{analytics.questions_count} questions in this survey</p>
                    {analytics.question_stats.length > 0 && (
                      <div className="space-y-2">
                        {analytics.question_stats.map((q, i) => (
                          <div key={q.id} className="flex items-center justify-between p-3 bg-slate-800 rounded-lg">
                            <span className="text-slate-300">Q{i + 1}: {q.text || '(No text)'}</span>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-slate-700 text-slate-300">{q.type}</Badge>
                              {q.required && <Badge className="bg-red-500/10 text-red-400">Required</Badge>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Data Analysis Tab */}
          <TabsContent value="analysis" className="space-y-6" data-testid="data-analysis-content">
            {!analysisData ? (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="py-12 text-center">
                  <Activity className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                  <p className="text-slate-400">
                    {user?.role === 'administrator' || user?.role === 'agency'
                      ? 'No analysis data available. Responses are needed to generate analysis.'
                      : 'Data analysis is available for administrators and agency users only.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400">Total Responses</p>
                      <p className="text-2xl font-bold text-white" data-testid="analysis-total-responses">{analysisData.total_responses}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400">Questions Analyzed</p>
                      <p className="text-2xl font-bold text-cyan-400">{analysisData.total_questions}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-400">Response Rate</p>
                      <p className="text-2xl font-bold text-emerald-400">
                        {analytics ? `${Math.round(analysisData.total_responses / (analytics.assigned_users_count || 1) * 100)}%` : '-'}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Per-Question Analysis */}
                {analysisData.questions.map((q, qi) => {
                  const CHART_COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#ec4899', '#3b82f6', '#14b8a6'];
                  const hasDistribution = q.distribution && q.distribution.length > 0;
                  const hasNumericStats = q.mean !== undefined;
                  // multiple_choice → bar only; single_choice/yes_no → bar + pie; rating/likert → bar + stats
                  const showPie = hasDistribution && q.type !== 'multiple_choice';

                  return (
                    <Card key={q.question_id} className="bg-slate-900 border-slate-800" data-testid={`analysis-question-${qi}`}>
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-white text-base">
                            Q{qi + 1}: {q.text}
                          </CardTitle>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-slate-700 text-slate-300 text-xs">{q.type.replace('_', ' ')}</Badge>
                            {q.required && <Badge className="bg-red-500/10 text-red-400 text-xs">Required</Badge>}
                          </div>
                        </div>
                        <div className="flex gap-4 text-xs text-slate-500 mt-1">
                          <span>{q.answered} answered</span>
                          <span>{q.skipped} skipped</span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {hasDistribution && (
                          <div className={`grid grid-cols-1 ${showPie ? 'lg:grid-cols-2' : ''} gap-6`}>
                            {/* Bar Chart */}
                            <div className={showPie ? '' : 'lg:col-span-1'}>
                              <p className="text-xs text-slate-500 mb-2 font-medium">Frequency Distribution</p>
                              <ResponsiveContainer width="100%" height={260}>
                                <BarChart data={q.distribution} margin={{ top: 20, right: 10, left: -10, bottom: 5 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                  <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 11 }} interval={0} angle={q.distribution.length > 4 ? -20 : 0} textAnchor={q.distribution.length > 4 ? "end" : "middle"} height={60} />
                                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} allowDecimals={false} />
                                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }}
                                    formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Responses']} />
                                  <Bar dataKey="count" name="Responses" radius={[4, 4, 0, 0]}
                                    label={{ position: 'top', fill: '#cbd5e1', fontSize: 11, formatter: (v) => {
                                      const d = q.distribution.find(x => x.count === v);
                                      return d ? `${v} (${d.percentage}%)` : v;
                                    }}}>
                                    {q.distribution.map((_, idx) => (
                                      <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              </ResponsiveContainer>
                            </div>

                            {/* Pie Chart - only for single_choice, yes_no */}
                            {showPie && (
                              <div>
                                <p className="text-xs text-slate-500 mb-2 font-medium">Percentage Breakdown</p>
                                <ResponsiveContainer width="100%" height={260}>
                                  <PieChart>
                                    <Pie
                                      data={q.distribution.filter(d => d.count > 0)}
                                      cx="50%" cy="50%"
                                      innerRadius={50} outerRadius={85}
                                      dataKey="count"
                                      nameKey="label"
                                      label={({ label, count, percentage }) => `${label}: ${count} (${percentage}%)`}
                                      labelLine={{ stroke: '#64748b' }}
                                    >
                                      {q.distribution.filter(d => d.count > 0).map((_, idx) => (
                                        <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
                                      ))}
                                    </Pie>
                                    <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px', color: '#fff' }}
                                      formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, name]} />
                                  </PieChart>
                                </ResponsiveContainer>
                              </div>
                            )}

                            {/* Frequency Table */}
                            <div className="lg:col-span-2">
                              <p className="text-xs text-slate-500 mb-2 font-medium">Detailed Breakdown</p>
                              <div className="rounded-lg border border-slate-800 overflow-hidden">
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="bg-slate-800/80">
                                      <th className="text-left p-3 text-slate-400 font-medium">Option</th>
                                      <th className="text-right p-3 text-slate-400 font-medium">Count</th>
                                      <th className="text-right p-3 text-slate-400 font-medium">Percentage</th>
                                      <th className="text-left p-3 text-slate-400 font-medium w-1/3">Distribution</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {q.distribution.map((d, di) => (
                                      <tr key={di} className="border-t border-slate-800/50">
                                        <td className="p-3 text-slate-300">{d.label}</td>
                                        <td className="p-3 text-right text-white font-medium">{d.count}</td>
                                        <td className="p-3 text-right text-cyan-400 font-medium">{d.percentage}%</td>
                                        <td className="p-3">
                                          <div className="w-full bg-slate-800 rounded-full h-2">
                                            <div
                                              className="h-2 rounded-full transition-all"
                                              style={{ width: `${d.percentage}%`, backgroundColor: CHART_COLORS[di % CHART_COLORS.length] }}
                                            />
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                        )}

                        {hasNumericStats && (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">Mean</p>
                              <p className="text-lg font-bold text-cyan-400">{q.mean}</p>
                            </div>
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">Median</p>
                              <p className="text-lg font-bold text-purple-400">{q.median}</p>
                            </div>
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">Min</p>
                              <p className="text-lg font-bold text-emerald-400">{q.min}</p>
                            </div>
                            <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                              <p className="text-xs text-slate-500">Max</p>
                              <p className="text-lg font-bold text-amber-400">{q.max}</p>
                            </div>
                          </div>
                        )}

                        {q.type === 'text' || q.type === 'textarea' ? (
                          <div>
                            <p className="text-xs text-slate-500 mb-2 font-medium">Sample Responses ({q.answered} total)</p>
                            {q.sample_answers && q.sample_answers.length > 0 ? (
                              <div className="space-y-2">
                                {q.sample_answers.map((a, ai) => (
                                  <div key={ai} className="bg-slate-800/50 rounded-lg p-3 text-sm text-slate-300 italic">"{a}"</div>
                                ))}
                              </div>
                            ) : (
                              <p className="text-slate-600 text-sm">No responses yet</p>
                            )}
                          </div>
                        ) : null}

                        {/* Interpretation Box */}
                        {q.answered > 0 && (
                          <div className="mt-4 p-4 bg-slate-800/40 border border-slate-700/50 rounded-xl" data-testid={`interpretation-${qi}`}>
                            <p className="text-xs text-cyan-400 font-semibold uppercase tracking-wider mb-2">Interpretation</p>
                            <p className="text-sm text-slate-300 leading-relaxed">
                              {(() => {
                                if (hasDistribution) {
                                  const sorted = [...q.distribution].sort((a, b) => b.count - a.count);
                                  const top = sorted[0];
                                  const total = q.answered;
                                  if (q.type === 'multiple_choice') {
                                    return `Out of ${total} respondent${total > 1 ? 's' : ''}, "${top.label}" was the most selected option with ${top.count} selection${top.count > 1 ? 's' : ''} (${top.percentage}%). ${sorted.length > 1 ? `"${sorted[1].label}" followed with ${sorted[1].count} (${sorted[1].percentage}%).` : ''} Note: respondents could select multiple options.`;
                                  }
                                  return `Out of ${total} respondent${total > 1 ? 's' : ''}, the most common answer was "${top.label}" with ${top.count} response${top.count > 1 ? 's' : ''} (${top.percentage}%).${sorted.length > 1 && sorted[1].count > 0 ? ` "${sorted[1].label}" was second with ${sorted[1].count} (${sorted[1].percentage}%).` : ''}${sorted.filter(s => s.count === 0).length > 0 ? ` ${sorted.filter(s => s.count === 0).length} option${sorted.filter(s => s.count === 0).length > 1 ? 's' : ''} received no responses.` : ''}`;
                                }
                                if (hasNumericStats) {
                                  return `Across ${q.answered} response${q.answered > 1 ? 's' : ''}, the average value was ${q.mean} (median: ${q.median}). Values ranged from ${q.min} to ${q.max}.`;
                                }
                                if (q.type === 'text' || q.type === 'textarea') {
                                  return `${q.answered} respondent${q.answered > 1 ? 's' : ''} provided text responses out of ${q.total_responses} total. ${q.skipped > 0 ? `${q.skipped} respondent${q.skipped > 1 ? 's' : ''} skipped this question.` : ''}`;
                                }
                                return `${q.answered} out of ${q.total_responses} respondents answered this question.`;
                              })()}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </>
            )}
          </TabsContent>

          {/* Audit Trail Tab (Admin Only) */}
          <TabsContent value="audit" className="space-y-4" data-testid="audit-trail-content">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ScrollText className="w-5 h-5 text-cyan-400" />
                  Audit Trail
                </CardTitle>
                <p className="text-sm text-slate-400">Complete history of all actions performed on this survey</p>
              </CardHeader>
              <CardContent>
                {auditLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No audit events recorded yet.</p>
                    <p className="text-xs mt-1">Events will appear as actions are performed on this survey.</p>
                  </div>
                ) : (
                  <div className="relative">
                    {/* Timeline line */}
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-800" />
                    <div className="space-y-0">
                      {auditLogs.map((log, i) => {
                        const actionColors = {
                          survey_created: 'text-emerald-400 bg-emerald-500/20',
                          questions_updated: 'text-blue-400 bg-blue-500/20',
                          users_assigned: 'text-purple-400 bg-purple-500/20',
                          bulk_upload: 'text-amber-400 bg-amber-500/20',
                          response_submitted: 'text-cyan-400 bg-cyan-500/20',
                          consent_updated: 'text-pink-400 bg-pink-500/20',
                          mass_email_sent: 'text-orange-400 bg-orange-500/20',
                        };
                        const colorClass = actionColors[log.action] || 'text-slate-400 bg-slate-500/20';
                        const actionLabel = log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                        const ts = new Date(log.timestamp);

                        return (
                          <div key={i} className="relative pl-10 py-3 group" data-testid={`audit-entry-${i}`}>
                            <div className={`absolute left-2.5 w-3 h-3 rounded-full border-2 border-slate-950 ${colorClass.split(' ')[1]} z-10`} />
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2">
                                  <Badge className={`text-xs ${colorClass}`}>{actionLabel}</Badge>
                                  <span className="text-xs text-slate-500">by {log.actor}</span>
                                </div>
                                {log.details && (
                                  <p className="text-sm text-slate-400 mt-1">{log.details}</p>
                                )}
                              </div>
                              <span className="text-xs text-slate-600 whitespace-nowrap">
                                {ts.toLocaleDateString()} {ts.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Question Dialog */}
      <Dialog open={showAddQuestion} onOpenChange={setShowAddQuestion}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add Question</DialogTitle>
            <DialogDescription className="text-slate-400">Choose a question type to add to your survey.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-4">
            {questionTypes.map((type) => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => handleAddQuestion(type)}
                  className="flex items-start gap-3 p-4 rounded-xl bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 text-left transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-cyan-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white">{type.name}</p>
                    <p className="text-sm text-slate-400">{type.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={showAssignUsers} onOpenChange={(open) => { setShowAssignUsers(open); if (!open) setUserSearchQuery(''); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Users to Survey</DialogTitle>
            <DialogDescription className="text-slate-400">Select users who can access this survey.</DialogDescription>
          </DialogHeader>
          
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or role..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="pl-10 bg-slate-800 border-slate-700 text-white"
            />
          </div>
          
          {/* User count info */}
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">
              {allUsers.filter(u => {
                const query = userSearchQuery.toLowerCase();
                return !query || 
                  u.name?.toLowerCase().includes(query) || 
                  u.email?.toLowerCase().includes(query) ||
                  u.role?.toLowerCase().includes(query);
              }).length} users found
            </span>
            <span className="text-cyan-400">{selectedUserIds.length} selected</span>
          </div>
          
          <div className="py-2 max-h-[350px] overflow-y-auto space-y-2">
            {allUsers
              .filter(u => {
                const query = userSearchQuery.toLowerCase();
                return !query || 
                  u.name?.toLowerCase().includes(query) || 
                  u.email?.toLowerCase().includes(query) ||
                  u.role?.toLowerCase().includes(query);
              })
              .sort((a, b) => {
                const aSelected = selectedUserIds.includes(a.id) ? 0 : 1;
                const bSelected = selectedUserIds.includes(b.id) ? 0 : 1;
                if (aSelected !== bSelected) return aSelected - bSelected;
                return (a.name || '').localeCompare(b.name || '');
              })
              .map((u) => (
              <label key={u.id} className="flex items-center gap-3 p-3 rounded-lg bg-slate-800 cursor-pointer hover:bg-slate-700">
                <input
                  type="checkbox"
                  checked={selectedUserIds.includes(u.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedUserIds([...selectedUserIds, u.id]);
                    } else {
                      setSelectedUserIds(selectedUserIds.filter(id => id !== u.id));
                    }
                  }}
                  className="accent-cyan-500"
                />
                <div className="flex-1">
                  <p className="text-white">{u.role === 'doctor' ? `Dr. ${u.name}` : u.name}</p>
                  <p className="text-sm text-slate-500">{u.email}</p>
                </div>
                <Badge className={getRoleBadgeColor(u.role)}>{u.role}</Badge>
              </label>
            ))}
            {allUsers.filter(u => {
              const query = userSearchQuery.toLowerCase();
              return !query || 
                u.name?.toLowerCase().includes(query) || 
                u.email?.toLowerCase().includes(query) ||
                u.role?.toLowerCase().includes(query);
            }).length === 0 && (
              <div className="text-center py-8 text-slate-500">
                No users found matching "{userSearchQuery}"
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignUsers(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleAssignUsers} className="bg-gradient-to-r from-blue-500 to-cyan-400">
              Assign {selectedUserIds.length} Users
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Survey Preview</DialogTitle>
            <DialogDescription className="text-slate-400">This is how your survey will appear to respondents.</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div className="p-6 bg-slate-800 rounded-xl">
              <h2 className="text-xl font-bold text-white mb-2">{survey?.title}</h2>
              <p className="text-slate-400">{survey?.description}</p>
            </div>
            {questions.map((q, index) => (
              <div key={q.id} className="p-4 bg-slate-800 rounded-xl">
                <p className="font-medium text-white mb-3">
                  {index + 1}. {q.text || '(Question text)'}
                  {q.required && <span className="text-red-400 ml-1">*</span>}
                </p>
                {q.type === 'text' && <Input placeholder="Your answer..." className="bg-slate-700 border-slate-600" disabled />}
                {q.type === 'textarea' && <Textarea placeholder="Your answer..." className="bg-slate-700 border-slate-600" disabled />}
                {q.type === 'number' && <Input type="number" placeholder="0" className="bg-slate-700 border-slate-600 w-32" disabled />}
                {q.type === 'single_choice' && (
                  <div className="space-y-2">
                    {q.options.map((opt, i) => (
                      <label key={i} className="flex items-center gap-2 text-slate-300">
                        <input type="radio" disabled className="accent-cyan-500" />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}
                {q.type === 'multiple_choice' && (
                  <div className="space-y-2">
                    {q.options.map((opt, i) => (
                      <label key={i} className="flex items-center gap-2 text-slate-300">
                        <input type="checkbox" disabled className="accent-cyan-500" />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}
                {q.type === 'yes_no' && (
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 text-slate-300"><input type="radio" disabled /> Yes</label>
                    <label className="flex items-center gap-2 text-slate-300"><input type="radio" disabled /> No</label>
                  </div>
                )}
                {q.type === 'likert' && (
                  <div className="flex justify-between">
                    {['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree'].map((label, i) => (
                      <div key={i} className="text-center">
                        <input type="radio" disabled className="accent-cyan-500" />
                        <p className="text-xs text-slate-500 mt-1">{label}</p>
                      </div>
                    ))}
                  </div>
                )}
                {q.type === 'rating' && (
                  <div className="flex gap-2">
                    {[1, 2, 3, 4, 5].map(n => <Star key={n} className="w-8 h-8 text-slate-600" />)}
                  </div>
                )}
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowPreview(false)} className="bg-slate-800 hover:bg-slate-700">Close Preview</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mass Email Dialog */}
      <Dialog open={showMassEmail} onOpenChange={setShowMassEmail}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-400" /> Send Mass Email
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Send survey invitation emails to all {surveyUsers.length} assigned doctors with their login credentials.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Email Subject</Label>
              <Input
                value={emailTemplate.subject}
                onChange={(e) => setEmailTemplate({ ...emailTemplate, subject: e.target.value })}
                placeholder={`${survey?.title} - Survey Invitation`}
                className="bg-slate-800 border-slate-700 text-white mt-2"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email Body (Preview)</Label>
              <div className="mt-2 p-4 bg-slate-800 rounded-lg text-sm text-slate-300 space-y-2">
                <p>Dear Dr. [Name],</p>
                <p>You have been invited to participate in the survey: <strong>{survey?.title}</strong></p>
                <p className="text-slate-400">Your Login Credentials:</p>
                <p className="font-mono text-cyan-400">Username: [auto-generated]</p>
                <p className="font-mono text-cyan-400">Password: [auto-generated]</p>
                <p>Survey Link: <span className="text-cyan-400 underline">{window.location.origin}/surveys/{surveyId}/respond</span></p>
                <p className="text-slate-400 text-xs mt-4">This is a preview template. Each doctor will receive their individual credentials.</p>
              </div>
            </div>
            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <p className="text-blue-400 text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" /> Emails will include a tracking pixel to monitor open rates.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMassEmail(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleSendMassEmail} disabled={sendingEmails} className="bg-gradient-to-r from-blue-500 to-cyan-400">
              {sendingEmails ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Send to {surveyUsers.length} Doctors
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-cyan-400" /> Create & Add User to Survey
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a new user and automatically assign them to this survey.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Username *</Label>
                <Input
                  value={newUserForm.username}
                  onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                  placeholder="username"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Password *</Label>
                <Input
                  type="password"
                  value={newUserForm.password}
                  onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                  placeholder="••••••••"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Full Name</Label>
              <Input
                value={newUserForm.name}
                onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                placeholder="Dr. John Smith"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={newUserForm.email}
                onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                placeholder="doctor@hospital.com"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Phone Number</Label>
                <Input
                  value={newUserForm.phone}
                  onChange={(e) => setNewUserForm({ ...newUserForm, phone: e.target.value })}
                  placeholder="+1234567890"
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-slate-300">Role</Label>
                <Select value={newUserForm.role} onValueChange={(value) => setNewUserForm({ ...newUserForm, role: value })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800 text-white">
                    <SelectItem value="doctor" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Doctor</SelectItem>
                    <SelectItem value="agency" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Agency</SelectItem>
                    <SelectItem value="sponsor" className="text-white hover:bg-slate-800 focus:bg-slate-800 focus:text-white">Sponsor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-slate-300">Organization</Label>
              <Input
                value={newUserForm.organization}
                onChange={(e) => setNewUserForm({ ...newUserForm, organization: e.target.value })}
                placeholder="Hospital/Clinic Name"
                className="bg-slate-800 border-slate-700 text-white mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateUser(false)} className="border-slate-700">Cancel</Button>
            <Button onClick={handleCreateUserInSurvey} disabled={isCreatingUser} className="bg-gradient-to-r from-blue-500 to-cyan-400">
              {isCreatingUser ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create & Assign User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={showBulkUpload} onOpenChange={(open) => { setShowBulkUpload(open); if (!open) setBulkResult(null); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="w-5 h-5 text-cyan-400" /> Bulk Upload Doctors
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Upload a CSV or Excel file to create multiple doctor accounts and assign them to this survey.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-slate-800/50 rounded-lg border border-dashed border-slate-700">
              <p className="text-sm text-slate-300 mb-2">Required columns: <strong>Username, Password, Email</strong></p>
              <p className="text-sm text-slate-400">Optional: Full Name, Phone Number, Organization</p>
              <p className="text-xs text-slate-500 mt-2">All users will be created with the "Doctor" role.</p>
            </div>
            <div>
              <button
                onClick={async () => {
                  try {
                    const response = await api.get(`/surveys/${surveyId}/bulk-upload-template`, { responseType: 'blob' });
                    const url = URL.createObjectURL(response.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = 'bulk_upload_template.csv';
                    a.click();
                    URL.revokeObjectURL(url);
                  } catch (e) { alert('Failed to download template'); }
                }}
                className="text-cyan-400 hover:text-cyan-300 text-sm underline cursor-pointer"
                data-testid="download-template-link"
              >
                Download CSV Template
              </button>
            </div>
            <div>
              <Label className="text-slate-300">Select File</Label>
              <Input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleBulkUpload(file);
                }}
                className="bg-slate-800 border-slate-700 text-white mt-2 file:bg-slate-700 file:text-white file:border-0 file:mr-3"
                disabled={bulkUploading}
                data-testid="bulk-upload-file-input"
              />
            </div>
            {bulkUploading && (
              <div className="flex items-center gap-2 text-cyan-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Processing file...
              </div>
            )}
            {bulkResult && !bulkResult.error && (
              <div className="space-y-3">
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                  <p className="text-emerald-400 text-sm font-medium">{bulkResult.message}</p>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    <div className="text-center p-2 bg-slate-800 rounded">
                      <p className="text-emerald-400 font-bold text-lg">{bulkResult.created}</p>
                      <p className="text-slate-400">Created</p>
                    </div>
                    <div className="text-center p-2 bg-slate-800 rounded">
                      <p className="text-amber-400 font-bold text-lg">{bulkResult.existing_assigned}</p>
                      <p className="text-slate-400">Existing</p>
                    </div>
                    <div className="text-center p-2 bg-slate-800 rounded">
                      <p className="text-red-400 font-bold text-lg">{bulkResult.failed}</p>
                      <p className="text-slate-400">Failed</p>
                    </div>
                  </div>
                </div>
                {bulkResult.errors && bulkResult.errors.length > 0 && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm font-medium mb-1">Errors:</p>
                    {bulkResult.errors.map((err, i) => (
                      <p key={i} className="text-xs text-red-300">{err.username}: {err.reason}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
            {bulkResult?.error && (
              <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                <p className="text-red-400 text-sm">{bulkResult.error}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowBulkUpload(false); setBulkResult(null); }} className="border-slate-700">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurveyBuilderPage;
